// respuestasBotones.js — ESPEJO EXACTO de lo que contesta el backend cuando el alumno pulsa un botón de un
// mensaje automático de autoescuela. Lo usan las vistas previas INTERACTIVAS de «Mensajes automáticos»
// (app de autoescuelas y simulador de ventas). Funciones PURAS, sin red.
//
// De dónde sale cada texto (y __tests__/respuestasBotones.mirror.test.js lo compara con el backend):
//   - etiquetas «Confirmar práctica» / «Cancelar práctica»        ← c_processMessage/index.js (isPracticaButtonCandidate)
//   - «¡Perfecto! Tu práctica ha sido confirmada…»                ← c_processMessage/i18nStrings.js (practiceConfirmed.es),
//       SOLO con reminder_config.thank_on_confirm === true (markInboundRead.js); si no, solo se marca leído
//   - respuesta a «Cancelar práctica»                               ← c_processMessage/practicaCancelReply.js
//   - aviso del cargo por cancelación tardía                       ← c_offerPracticaSlot/index.js (applyLateCancelCharge)
//       con la plantilla practica_cargo_cancelacion(_trial) de c_sendTemplate
//   - «Lo quiero» / «Ahora no» del hueco libre y sus respuestas     ← c_offerPracticaSlot/index.js (modeAccept / modeReject)
//   - «Cambiar hora» → Flow de pedir o reservar clase, y sus acuses ← c_processMessage/pedirPractica.js
//   - resumen de la clase reservada                                ← c_pedirPracticaFlow/reservaFlow.js (resumenDe)
//   - prefijo «📩 *Autoescuela* te escribe:» del número compartido  ← c_sendMessage/index.js
//   - «Me viene bien» / «Quiero cambiar algo» (semana con botones)  ← c_processMessage/semanaBotones.js
//       «¡Perfecto…!» solo con ctx.gracias (thank_on_confirm) y «Recibido…» solo con ctx.acuseCambio (29/10/2026)
//     y sus variantes para la práctica asignada con botones (ctx.origen === "asignada")
//   - «Quiero cambiar algo» con huecos (ctx.cambio.modo === "huecos") ← c_processMessage/cambioHuecos.js
//     (los textos viven en flowCambiarPractica.js, fijados por su propio test espejo)
//   - textos de la imagen de la semana                             ← c_generateClaseConfirmacion/template.js (renderSemana)
//   - «Sí, voy» / «No puedo» de la propuesta de examen              ← c_processMessage/examenBotones.js (examenPropuesta.js)
//   - aviso de «Pierde la clase» (cancelación tardía con bono)       ← practica_cancelacion_bono_trial (tardiaBono.js)
// Si cambias un texto en el backend, cámbialo aquí: el test falla hasta que coincidan.
//
// VARIABLES: los datos que cambian con cada alumno viajan dentro del texto como ⟦clave⟧ (VAR). La vista
// previa los pinta como chips que rotan entre los VALORES de su clave; `rellenar` los sustituye por uno.
//
// COPIA POR VALOR (fichero idéntico) en apps/conext-ventas/src/simulator/pages/autoescuela/mensajes/
// respuestasBotones.js — un test del simulador compara los dos ficheros.

import { CAMBIO_TEXTOS, textoFlowCambio } from "./flowCambiarPractica";
// Catalán y «Quiero cambiar algo» = cancelar (8/10/2026, Garrotxa): ctx.idioma === "ca", ctx.cambio.modo === "cancelar".
import {
  botonEs, BOTON_CA, PRACTICA_CONFIRMADA_CA, practicaCancelReplyCa, SEMANA_CA, ASIGNADA_CA, HUECO_CA, PLANTILLAS_CA,
  TEXTOS_CANCELAR, listaCancelar, filaCancelar,
} from "./idiomaAvisos";
// Propuesta de examen «Sí, voy» / «No puedo» y cancelación tardía con BONO (9/10/2026, Náyade y Poveda).
import { BOTON_EXAMEN, esBotonExamen, respuestaExamen } from "./examenPropuesta";
import { politicaBono, textoSesionDescontada, pierdeSesion, politicaSinBono, textoClasePerdida } from "./tardiaBono";
// Rellenar huecos ADELANTANDO prácticas (14/10/2026, Wins #24f): «Adelantar» / «Dejarla como está».
import { esBotonAdelanto, respuestaAdelanto, BOTON_ADELANTO } from "./adelantarHuecos";
// Los avisos que son PLANTILLA (el del cargo) salen del catálogo con la a medida de la empresa (ctx.aMedida, 9/10/2026).
import { vistaPlantilla } from "./plantillasAutoescuela";
// «Hasta cuándo puede anular el alumno» (26/10/2026): el texto es el del backend (espejo por marcas).
import { textoCorteAlumno } from "./corteCancelacion";

// ── Variables (chips) ────────────────────────────────────────────────────────────────────────────
export const VAR = (clave) => `⟦${clave}⟧`;
const RE_VAR = /⟦(\w+)⟧/g;

// Valores de ejemplo de cada variable. El PRIMERO es el que se ve sin rotar (y el de los ejemplos fijos).
export const VALORES = {
  alumno: ["Pablo", "Lucía", "Andrea", "Hugo"],
  fecha: ["02/10/2026", "06/10/2026", "09/10/2026"],
  // La fecha larga del aviso de cancelación (c_cancelPractica.fechaAviso: «viernes, 2 de octubre»), los mismos días.
  fechaLarga: ["viernes, 2 de octubre", "martes, 6 de octubre", "viernes, 9 de octubre"],
  fechaLargaCa: ["divendres, 2 d'octubre", "dimarts, 6 d'octubre", "divendres, 9 d'octubre"],
  hora: ["17:00", "09:30", "12:00", "18:30"],
  fechaAnt: ["02/10/2026", "06/10/2026", "09/10/2026"],
  horaAnt: ["17:00", "09:30", "12:00"],
  fechaNueva: ["03/10/2026", "07/10/2026", "13/10/2026"],
  horaNueva: ["18:00", "10:30", "16:00"],
  tipo: ["Ciudad", "Autovía", "Carretera"],
  profesor: ["Marta", "Javier", "Rosa"],
  motivo: ["Coche en el taller", "Profesor de baja", "Festivo local"],
  examen: ["práctico", "teórico"],
  resultado: ["APTO", "NO APTO"],
  fechaExamen: ["15/10/2026", "22/10/2026", "29/10/2026"],
  horaExamen: ["10:00", "08:30", "11:15"],
  concepto: ["el bono de 10 prácticas", "la matrícula", "la tasa de examen"],
  importe: ["350,00 €", "150,00 €", "94,05 €"], // el de cada concepto (página de pago)
  factura: ["FAC-2026-0007", "FAC-2026-0012", "FAC-2026-0031"],
  quedan: ["2", "1", "3"],
  fechaCap: ["15/03/2027", "02/06/2027", "20/11/2026"],
  // Día y hora de la práctica tal como los escribe c_processMessage/semanaBotones.js (cuandoPractica).
  cuando: ["viernes 2/10 a las 17:00", "martes 6/10 a las 09:30", "viernes 9/10 a las 12:00"],
  // Lo mismo en catalán (cuandoPractica(start, "ca")) y el tipo de fábrica en catalán.
  cuandoCa: ["divendres 2/10 a les 17:00", "dimarts 6/10 a les 09:30", "divendres 9/10 a les 12:00"],
  tipoCa: ["Ciutat", "Autovia", "Carretera"],
  permiso: ["B", "A2", "C"],
  // La práctica que el alumno ya tenía y adelanta (oferta de adelantar, 14/10/2026): siempre DESPUÉS de fechaLarga.
  fechaSuya: ["lunes, 5 de octubre", "jueves, 8 de octubre", "lunes, 12 de octubre"],
  fechaSuyaCa: ["dilluns, 5 d'octubre", "dijous, 8 d'octubre", "dilluns, 12 d'octubre"],
  horaSuya: ["18:00", "10:00", "16:30"],
};

export function valorDe(clave, tick = 0) {
  const lista = VALORES[clave];
  if (!lista || !lista.length) return clave;
  const n = Math.abs(Math.trunc(Number(tick) || 0));
  return lista[n % lista.length];
}

/** El texto con cada ⟦clave⟧ sustituida por su valor en la vuelta `tick`. */
export function rellenar(texto, tick = 0) {
  return String(texto == null ? "" : texto).replace(RE_VAR, (_, k) => valorDe(k, tick));
}

/** Trozos de un texto: [{ texto }] y [{ variable }] alternados, para pintar los chips. */
export function trozos(texto) {
  const out = [];
  const s = String(texto == null ? "" : texto);
  let last = 0;
  s.replace(RE_VAR, (m, k, idx) => {
    if (idx > last) out.push({ texto: s.slice(last, idx) });
    out.push({ variable: k });
    last = idx + m.length;
    return m;
  });
  if (last < s.length) out.push({ texto: s.slice(last) });
  return out;
}

// ── Botones (las etiquetas que llegan como quick reply) ──────────────────────────────────────────
export const BOTON = {
  confirmar: "Confirmar práctica",
  cancelar: "Cancelar práctica",
  cambiarHora: "Cambiar hora",
  loQuiero: "Lo quiero",
  ahoraNo: "Ahora no",
  meVieneBien: "Me viene bien",
  quieroCambiar: "Quiero cambiar algo",
  pagar: "Pagar ahora",
  resena: "Dejar reseña",
};

// ── Textos del backend ──────────────────────────────────────────────────────────────────────────
/** c_sendMessage: por el número compartido, todo texto libre sale con el nombre de la autoescuela delante. */
export const prefijoConext = (negocio) => `📩 *${negocio}* te escribe:\n\n`;

/** i18nStrings.practiceConfirmed.es (solo si la autoescuela tiene «dar las gracias al confirmar»). */
export const PRACTICA_CONFIRMADA = "¡Perfecto! Tu práctica ha sido confirmada. Te esperamos en la fecha y hora acordada.";

const E164 = /^\+[1-9]\d{7,14}$/;

/** practicaCancelReply.formatPhone: «+34600111222» → «+34 600 11 12 22» (España); el resto tal cual. */
export function formatPhone(phone) {
  const m = /^\+34(\d{3})(\d{2})(\d{2})(\d{2})$/.exec(phone);
  return m ? `+34 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : phone;
}

/** practicaCancelReply (castellano), con el teléfono de «Si el alumno cancela» (vacío o mal → «escríbenos por aquí»). */
export function practicaCancelReply(phone) {
  const p = typeof phone === "string" ? phone.trim() : "";
  if (E164.test(p)) {
    return `Hemos cancelado tu próxima práctica. Si quieres cambiarla por otro día, llámanos o escríbenos al ${formatPhone(p)}.`;
  }
  return "Hemos cancelado tu próxima práctica. Si quieres cambiarla por otro día, escríbenos por aquí y te buscamos otro hueco.";
}

/** Cuerpo de practica_cargo_cancelacion (c_offerPracticaSlot.applyLateCancelCharge). */
export const CARGO_CUERPO = "Hola {{1}},\n\nTu práctica del {{2}} a las {{3}} se ha cancelado con menos de {{4}} horas de antelación.\n\nSegún las condiciones del centro, se ha añadido un cargo de {{5}} a tu cuenta. Puedes consultarlo con nosotros por este chat.";

/** El importe tal como lo escribe applyLateCancelCharge. */
export const eurosCargo = (cents) => `${((Number(cents) || 0) / 100).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €`;

/** El aviso del cargo; por el número compartido va la variante _trial («📩 *X* te informa:»). */
export function textoCargo({ conext = true, negocio, nombre, fecha, hora, horas, importe, idioma, aMedida = null } = {}) {
  const vals = [nombre, fecha, hora, String(horas), importe];
  // Por el número compartido: practica_cargo_cancelacion_trial tal cual la manda c_sendTemplate (catálogo) y, si la
  // autoescuela tiene la suya a medida, la suya.
  if (conext && aMedida && Object.keys(aMedida).length) {
    const v = vistaPlantilla("practica_cargo_cancelacion_trial", { idioma: idioma === "ca" ? "ca" : "es", vals: [negocio, ...vals], aMedida });
    if (v) return v.message;
  }
  // En catalán solo existe la _trial (número compartido): practica_cargo_cancelacion_trial [ca].
  if (idioma === "ca" && conext) {
    return PLANTILLAS_CA.practica_cargo_cancelacion_trial.body.replace(/\{\{(\d)\}\}/g, (_, n) => [negocio, ...vals][Number(n) - 1]);
  }
  const cuerpo = CARGO_CUERPO.replace(/\{\{(\d)\}\}/g, (_, n) => vals[Number(n) - 1]);
  return conext ? `📩 *${negocio}* te informa:\n\n${cuerpo}` : cuerpo;
}

/** c_offerPracticaSlot: lo que recibe quien pulsa «Lo quiero». «Ahora no» no tiene respuesta (solo se anota). */
export const HUECO = {
  ganada: "¡Hecho! La práctica queda a tu nombre. Te esperamos.",
  otro: "Lo sentimos, otro alumno se ha quedado ya con esa práctica. Te avisaremos en cuanto se libere otra.",
  plazo: "Esa práctica ya no está disponible: el plazo para cogerla ha pasado. Te avisaremos en cuanto se libere otra.",
};

/**
 * Corte de cancelación del alumno (26/10/2026): ctx.corte = { horas } y ctx.dentroCorte (conmutador «Anula a última
 * hora»). Lo que le llega si intenta anular dentro del corte: no se anula, y a la oficina, la petición. null si no aplica.
 */
function respuestaCorte(ctx, { deNegocio, ca, cuando, vacio }) {
  const h = Number(ctx.corte && ctx.corte.horas) || 0;
  if (!(h > 0) || ctx.dentroCorte !== true) return null;
  const telOk = typeof ctx.telefono === "string" && E164.test(ctx.telefono.trim());
  return {
    ...vacio,
    mensajes: [{ texto: deNegocio(textoCorteAlumno({ horas: h, lang: ca ? "ca" : "es", cuando, telefono: telOk ? formatPhone(ctx.telefono.trim()) : "" })) }],
    crm: [
      nota("crmCorteNoAnula", "No se anula: le faltan menos de {{h}} h. La práctica sigue en pie.", { h }),
      nota("crmCortePeticion", "Te llega a Planificación → Peticiones como «Cancelación del alumno · fuera de plazo» (y a la campanita) para que decidas tú. Desde la app la puedes anular siempre."),
    ],
  };
}

/** pedirPractica.sendPedirPracticaFlow: el mensaje con el Flow que llega al pulsar «Cambiar hora». */
// `id`: qué formulario abre el botón en la vista previa (FlowsAlumno.jsx).
export const FLOW_PEDIR = { texto: "Dinos qué clase quieres y cuándo te viene bien. Te escribimos para colocarla.", pie: "Pedir clase", cta: "Pedir clase", id: "pedir" };
export const FLOW_RESERVAR = { texto: "Elige el día y la hora que te vengan bien entre las horas libres. La clase queda reservada al momento.", pie: "Reservar clase", cta: "Reservar clase", id: "reservar" };

export const TIPO_TEXTO = {
  ciudad: "circulación en ciudad",
  carretera: "carretera",
  autovia: "autovía",
  cualquiera: "cualquier tipo",
};
export const FRANJA_TEXTO = { manana: "por la mañana", tarde: "por la tarde", cualquiera: "" };

/** pedirPractica.buildAcuseText: acuse de la petición enviada desde el Flow. */
export function buildAcuseText(peticion, { nombre } = {}) {
  const saludo = nombre ? `Petición recibida, ${nombre}.` : "Petición recibida.";
  const partes = [];
  partes.push(`Clase de ${TIPO_TEXTO[peticion.tipo] || peticion.tipo}`);
  if (peticion.dias && peticion.dias.length) {
    partes.push(peticion.dias.length === 7 ? "cualquier día" : peticion.dias.join(", "));
  }
  const franja = FRANJA_TEXTO[peticion.franja];
  if (franja) partes.push(franja);
  return (
    `✅ ${saludo}\n\n` +
    `${partes.join(" · ")}\n\n` +
    "Te escribimos en cuanto tengamos hueco para colocarla. No hace falta que hagas nada más."
  );
}

/** pedirPractica.buildReservaAcuseText: acuse de la clase reservada desde el Flow de reserva. */
export function buildReservaAcuseText(resumen, tipo) {
  const nota = tipo === "por_definir" ? "\n\nTu profesor te dirá qué tipo de práctica toca (ciudad, carretera o autovía)." : "";
  return `✅ ¡Clase reservada!\n\n${resumen}${nota}\n\nTe mandaremos un recordatorio antes de la clase. Si al final no puedes ir, cancélala con el botón del recordatorio.`;
}

// Ejemplos que rellena el alumno en los Flows de la vista previa.
export const PETICION_EJEMPLO = { tipo: "ciudad", dias: ["martes", "jueves"], franja: "tarde" };
/** reservaFlow.resumenDe({ tipo: "ciudad", slot: { start: martes 6/10 17:00 }, nombres: Marta }). */
export const RESUMEN_RESERVA_EJEMPLO = "Clase de Ciudad el martes 6 de octubre a las 17:00 con Marta.";

/** Planificación semanal con botones (practicas_semana_crm_trial_img_btn / _trial_btn). */
export const SEMANA = {
  meVieneBien: (nombre) => `¡Perfecto, ${nombre}! Queda confirmada tu semana. Te recordaremos cada práctica antes de la clase.`,
  quieroCambiar: "Claro. Escríbenos aquí qué quieres cambiar (por ejemplo: «el martes no puedo, ¿puede ser el miércoles por la tarde?») y la autoescuela te contesta.",
  recibido: "Recibido. Se lo pasamos a la autoescuela y te contestan por aquí.",
  ejemplo: "El martes no puedo, ¿puede ser el miércoles por la tarde?",
};

/**
 * Práctica asignada con los mismos botones (practica_asignada_crm_trial(_img)_btn, casilla «Incluir botón para
 * que el alumno confirme»). Es UNA práctica: c_processMessage/semanaBotones.js contesta con estos textos
 * cuando el item del wamid lleva `tipo: "asignada"`. «Recibido…» es el mismo que el de la semana.
 */
export const ASIGNADA = {
  // Con la fecha y hora ACTUALES de la práctica (textoOkConFecha): si se movió sin volver a preguntar, se ve.
  meVieneBien: (nombre, cuando) => `¡Perfecto, ${nombre}! Queda confirmada tu práctica${cuando ? ` del ${cuando}` : ""}. Te la recordaremos antes de la clase.`,
  quieroCambiar: "Claro. Escríbenos aquí qué quieres cambiar (por ejemplo: «a esa hora no puedo, ¿puede ser por la tarde?») y la autoescuela te contesta.",
  ejemplo: "A esa hora no puedo, ¿puede ser por la tarde?",
};

/** Textos fijos de la imagen de la semana (c_generateClaseConfirmacion/template.js, renderSemana). */
export const IMAGEN_SEMANA = {
  eyebrow: "Semana",
  titulo: "Tus prácticas de la semana",
  pie: "Para cualquier cambio o cancelación, contacta con tu autoescuela.",
  accent: "#1D4ED8",
};

// ── Qué pasa al pulsar un botón ─────────────────────────────────────────────────────────────────
// Notas «en el CRM»: { k, d, o } (clave de traducción, texto en castellano y variables).
const nota = (k, d, o) => (o ? { k, d, o } : { k, d });

/**
 * Lo que le llega al alumno (y lo que pasa en el CRM) al pulsar `boton`.
 *
 * ctx: { conext, negocio, telefono (cancel_contact_phone), gracias (thank_on_confirm: también «Me viene bien»),
 *        acuseCambio (acuse_cambio.enabled: «Recibido…» al texto de cambio), origen ("asignada"),
 *        tardia: { enabled, mode, hours, amount_cents, con_bono }, cancelaTarde (simulación), conBono (simulación:
 *        la práctica se pagó con bono), examen ({ tipo, fecha, hora }: la propuesta de examen),
 *        huecos: { enabled, response_minutes }, resultadoHueco: "gana" | "otro" | "plazo",
 *        reservaOnline, fecha, hora }
 *
 * Devuelve { mensajes: [{ texto, flow?: { cta, pie }, separador? }], leido, url, crm: [nota], escribir? }
 *   - leido: no se contesta, pero el botón queda marcado como leído (doble check azul).
 *   - url: el botón abre una página (no manda nada al chat): "pago" o "resena" (PaginasEnlace.jsx).
 *   - escribir: el alumno puede escribir a continuación ({ ejemplo, respuesta, crm }); respuesta null = no se le
 *     contesta y su mensaje sale como leído.
 */
export function respuestaBoton(botonPulsado, ctx = {}) {
  const negocio = ctx.negocio || "Tu autoescuela";
  const deNegocio = (txt) => (ctx.conext === false ? txt : `${prefijoConext(negocio)}${txt}`);
  const alumno = VAR("alumno");
  const vacio = { mensajes: [], leido: false, url: false, crm: [] };
  // Idioma de la autoescuela (c_autoescuela_config.idioma_mensajes): las respuestas de texto salen en catalán aunque
  // las plantillas aún no estén aprobadas. Los botones en catalán llevan de payload la etiqueta en castellano.
  const ca = ctx.idioma === "ca";
  const boton = botonEs(botonPulsado);
  // Oferta de adelantar su práctica (14/10/2026): mismos resultados simulables que «Lo quiero».
  if (esBotonAdelanto(botonPulsado)) {
    const r = ctx.resultadoHueco === "otro" || ctx.resultadoHueco === "plazo" ? ctx.resultadoHueco : "gana";
    return respuestaAdelanto(botonPulsado, { deNegocio, ca, resultado: r, saltos: ctx.huecos ? ctx.huecos.advance_max_chain : 2 });
  }
  // «Indicar disponibilidad» (14/10/2026): es un botón FLOW — abre el formulario «Tu disponibilidad» en el móvil, sin
  // escribir nada en el chat (un solo mensaje: el aviso ya lleva el botón). Al guardarlo, el acuse de c_processMessage.
  if (botonPulsado === "Indicar disponibilidad" || botonPulsado === "Indicar disponibilitat") {
    return { ...vacio, crm: [nota("crmDisponibilidad", "El botón abre el formulario «Tu disponibilidad» en su móvil (no escribe nada en el chat): marca días y franjas y cuántas prácticas quiere. Al guardarlo se pone sola en su ficha, te avisa la campanita y le llega «Disponibilidad guardada».")] };
  }
  // Una fila de la lista «qué práctica cancelas» (modo «cancelar»).
  const fila = filaCancelar(botonPulsado);
  // Propuesta de examen: «Sí, voy» / «No puedo» (o su etiqueta en catalán).
  if (esBotonExamen(botonPulsado)) {
    const si = botonEs(botonPulsado) === BOTON_EXAMEN.si || botonPulsado === BOTON_EXAMEN.si || botonPulsado === "Sí, hi aniré";
    const texto = respuestaExamen(botonPulsado, { nombre: alumno, examen: ctx.examen || { tipo: "practico", fecha: "2026-10-20", hora: "10:00" }, lang: ca ? "ca" : "es" });
    return {
      ...vacio,
      mensajes: [{ texto: deNegocio(texto) }],
      crm: si
        ? [nota("crmExamenSi", "En el CRM: el examen queda como «Va» en su ficha y en la convocatoria, y te llega a la campanita.")]
        : [nota("crmExamenNo", "En el CRM: el examen queda como «No puede» y te llega a Planificación → Peticiones (y a la campanita) para buscarle otra fecha.")],
    };
  }
  if (fila) {
    const tx = TEXTOS_CANCELAR[fila.lang];
    if (fila.ninguna) return { ...vacio, mensajes: [{ texto: deNegocio(tx.nadaCancelado) }], crm: [nota("crmCancelarNinguna", "No se cancela nada.")] };
    const corte = respuestaCorte(ctx, { deNegocio, ca: fila.lang === "ca", cuando: fila.cuando, vacio });
    if (corte) return corte;
    return {
      ...vacio,
      mensajes: [{ texto: deNegocio(tx.hecho(fila.cuando)) }],
      crm: [nota("crmCancelacionAlumno", "En el CRM: la práctica queda cancelada por el alumno (se aplica tu cancelación tardía) y te llega a Planificación → Peticiones como «Cancelación del alumno». No se le ofrecen otras horas.")],
    };
  }

  switch (boton) {
    case BOTON.confirmar: {
      const crm = [nota("crmConfirmada", "En el CRM: la práctica queda Confirmada (borde verde en Planificación).")];
      if (ctx.gracias === true) return { ...vacio, mensajes: [{ texto: deNegocio(ca ? PRACTICA_CONFIRMADA_CA : PRACTICA_CONFIRMADA) }], crm };
      return {
        ...vacio,
        leido: true,
        crm: [...crm, nota("crmSinGracias", "No se le contesta (solo ve su mensaje como leído): así no gastas un mensaje. Si quieres darle las gracias, actívalo en los ajustes del recordatorio.")],
      };
    }
    case BOTON.cancelar: {
      const corte = respuestaCorte(ctx, { deNegocio, ca, cuando: `${ctx.fecha || VAR("fecha")} ${ca ? "a les" : "a las"} ${ctx.hora || VAR("hora")}`, vacio });
      if (corte) return corte;
      const tardia = ctx.tardia || {};
      const huecos = ctx.huecos || {};
      const tarde = tardia.enabled === true && ctx.cancelaTarde === true;
      const cents = Math.max(0, parseInt(tardia.amount_cents, 10) || 0);
      // Pagada con bono (simulación): con «Pierde la clase» o «Se le devuelve, sin cargo» no hay cargo en euros.
      const polBono = ctx.conBono === true ? politicaBono(tardia) : null;
      // Sin bono con «Pierde la clase» (19/10/2026, Carian): cuenta como dada, sin cargo.
      const pierdeSinBono = tarde && !polBono && politicaSinBono(tardia) === "pierde";
      const cobra = tarde && tardia.mode === "charge" && cents > 0 && (!polBono || polBono === "cobrar") && !pierdeSinBono;
      const telOk = typeof ctx.telefono === "string" && E164.test(ctx.telefono.trim());
      const mensajes = [{ texto: deNegocio(ca ? practicaCancelReplyCa(telOk ? formatPhone(ctx.telefono.trim()) : "") : practicaCancelReply(ctx.telefono)) }];
      const crm = [nota("crmCancelada", "En el CRM: la práctica se cancela sola y el hueco queda libre.")];
      if (huecos.enabled === true) crm.push(nota("crmHuecoOfrecido", "Como tienes «Rellenar huecos», ese hueco se ofrece por WhatsApp a otros alumnos."));
      if (cobra) {
        const importe = eurosCargo(cents);
        mensajes.push({
          separador: huecos.enabled === true
            ? nota("sepSiNadie", "Si nadie coge el hueco en {{min}} min", { min: Number(huecos.response_minutes) || 20 })
            : null,
          texto: textoCargo({ conext: ctx.conext !== false, negocio, nombre: alumno, fecha: ctx.fecha || VAR("fecha"), hora: ctx.hora || VAR("hora"), horas: Number(tardia.hours) > 0 ? Number(tardia.hours) : 24, importe, idioma: ctx.idiomaPlantillas, aMedida: ctx.aMedida }),
        });
        crm.push(nota("crmCargo", "Queda como cancelación tardía y se le apunta un cargo de {{importe}} en Pagos.", { importe }));
        if (polBono) crm.push(nota("crmBonoDevuelto", "La sesión del bono se le devuelve."));
        if (huecos.enabled === true) crm.push(nota("crmCargoPerdonado", "Si otro alumno se queda el hueco, no se le cobra."));
      } else if (tarde && pierdeSesion(polBono)) {
        const siempre = polBono === "pierde_siempre";
        // UN SOLO MENSAJE (19/10/2026): el aviso de pérdida, en el acto y EN VEZ de la respuesta de cancelación.
        mensajes.splice(0, mensajes.length, {
          texto: textoSesionDescontada({ negocio, nombre: alumno, fecha: ctx.fecha || VAR("fecha"), hora: ctx.hora || VAR("hora"), horas: Number(tardia.hours) > 0 ? Number(tardia.hours) : 24, idioma: ctx.idiomaPlantillas, aMedida: ctx.aMedida }),
        });
        crm.push(nota("crmBonoPierde", "Queda como cancelación tardía: la sesión se descuenta de su bono y no se le cobra nada en euros."));
        crm.push(nota("crmUnSoloMensaje", "Le llega solo este mensaje, en vez del aviso de cancelación: uno, no dos."));
        if (huecos.enabled === true) {
          crm.push(siempre
            ? nota("crmBonoPierdeSiempreHueco", "Aunque otro alumno se quede el hueco, la sesión NO se le devuelve: cuenta como clase dada.")
            : nota("crmBonoPierdeHueco", "Si otro alumno se queda el hueco, se le devuelve la sesión."));
        }
      } else if (pierdeSinBono) {
        // «Pierde la clase» SIN bono (19/10/2026, Carian): un solo mensaje, sin texto de cargo.
        mensajes.splice(0, mensajes.length, {
          texto: textoClasePerdida({ negocio, nombre: alumno, fecha: ctx.fecha || VAR("fecha"), hora: ctx.hora || VAR("hora"), horas: Number(tardia.hours) > 0 ? Number(tardia.hours) : 24, idioma: ctx.idiomaPlantillas, aMedida: ctx.aMedida }),
        });
        crm.push(nota("crmSinBonoPierde", "Queda como clase perdida: cuenta como dada y no se le apunta nada en su cuenta."));
        crm.push(nota("crmUnSoloMensaje", "Le llega solo este mensaje, en vez del aviso de cancelación: uno, no dos."));
        if (huecos.enabled === true) crm.push(nota("crmSinBonoPierdeHueco", "Aunque otro alumno se quede el hueco, la clase le cuenta como dada."));
      } else if (tarde && polBono === "devolver") {
        crm.push(nota("crmBonoDevolver", "Queda apuntada como cancelación tardía: se le devuelve la sesión del bono, sin cargo y sin más mensajes."));
      } else if (tarde) {
        crm.push(nota("crmTardiaRegistrada", "Queda apuntada como cancelación tardía, sin cobro: no se le manda nada más."));
      }
      return { ...vacio, mensajes, crm };
    }
    case BOTON.cambiarHora: {
      const f = ctx.reservaOnline ? FLOW_RESERVAR : FLOW_PEDIR;
      return {
        ...vacio,
        mensajes: [{ texto: deNegocio(f.texto), flow: { cta: f.cta, pie: f.pie, id: f.id } }],
        crm: [ctx.reservaOnline
          ? nota("crmCambiarHoraReserva", "La práctica no se mueve: el alumno abre el formulario y reserva otra clase entre tus horas libres.")
          : nota("crmCambiarHora", "La práctica no se mueve: el alumno abre el formulario y pide otra hora.")],
      };
    }
    case FLOW_PEDIR.cta:
      return {
        ...vacio,
        mensajes: [{ texto: deNegocio(buildAcuseText(PETICION_EJEMPLO, { nombre: alumno })) }],
        crm: [nota("crmPeticion", "En el CRM: la petición llega a Planificación → Peticiones; la clase la colocas tú.")],
      };
    case FLOW_RESERVAR.cta:
      return {
        ...vacio,
        mensajes: [{ texto: deNegocio(buildReservaAcuseText(RESUMEN_RESERVA_EJEMPLO, "ciudad")) }],
        crm: [nota("crmReservada", "En el CRM: la clase queda reservada en Planificación, con su recordatorio.")],
      };
    case BOTON.loQuiero: {
      const r = ctx.resultadoHueco === "otro" || ctx.resultadoHueco === "plazo" ? ctx.resultadoHueco : "gana";
      const crm = {
        gana: nota("crmHuecoGanado", "En el CRM: la práctica pasa a su nombre en Planificación. A los demás avisados se les dice que ya está cogida."),
        otro: nota("crmHuecoOtro", "Otro alumno pulsó antes «Lo quiero»: la práctica es suya y a este no se le asigna nada."),
        plazo: nota("crmHuecoPlazo", "Ya había pasado el tiempo para responder: no se le asigna nada."),
      }[r];
      return { ...vacio, mensajes: [{ texto: deNegocio((ca ? HUECO_CA : HUECO)[r === "gana" ? "ganada" : r]) }], crm: [crm] };
    }
    case BOTON.ahoraNo:
      return { ...vacio, crm: [nota("crmAhoraNo", "No se le contesta: su respuesta queda anotada y el hueco se sigue ofreciendo a los demás.")] };
    // ctx.origen === "asignada": los mismos botones en el aviso de práctica asignada (una sola práctica).
    // Recorte del 29/10/2026: el «¡Perfecto…!» sale SOLO con «Contestar al alumno cuando confirma» (ctx.gracias =
    // reminder_config.thank_on_confirm, el mismo interruptor que el «Confirmar práctica» del recordatorio). Si no, la
    // práctica se confirma igual y su botón sale como leído (semanaBotones.js → marcarLeido).
    case BOTON.meVieneBien: {
      const crmOk = ctx.origen === "asignada"
        ? nota("crmAsignadaOk", "En el CRM: el alumno ha dado el visto bueno a esta práctica.")
        : nota("crmSemanaOk", "En el CRM: el alumno ha dado el visto bueno a su semana.");
      if (ctx.gracias !== true) {
        return {
          ...vacio,
          leido: true,
          crm: [crmOk, nota("crmSinGraciasSemana", "No se le contesta (ve su mensaje como leído): así no gastas un mensaje. Si quieres que le llegue «Perfecto, queda confirmada», marca «Contestar al alumno cuando confirma».")],
        };
      }
      return {
        ...vacio,
        mensajes: [{ texto: deNegocio(ca
          ? (ctx.origen === "asignada" ? ASIGNADA_CA.meVieneBien(alumno, VAR("cuandoCa")) : SEMANA_CA.meVieneBien(alumno))
          : (ctx.origen === "asignada" ? ASIGNADA.meVieneBien(alumno, VAR("cuando")) : SEMANA.meVieneBien(alumno))) }],
        crm: [crmOk],
      };
    }
    case BOTON.quieroCambiar: {
      const S = ca ? SEMANA_CA : SEMANA;
      const A = ca ? ASIGNADA_CA : ASIGNADA;
      // Recorte del 29/10/2026: el «Recibido…» tras su texto sale SOLO con ctx.acuseCambio
      // (c_autoescuela_config.acuse_cambio.enabled). Sin él, `respuesta: null` = su mensaje sale como leído.
      const acuse = ctx.acuseCambio === true ? deNegocio(S.recibido) : null;
      const crmCambio = [nota("crmSemanaCambio", "En el CRM: su mensaje llega a Planificación → Peticiones.")];
      if (!acuse) crmCambio.push(nota("crmSinAcuseCambio", "No se le contesta «Recibido» (ve su mensaje como leído): ya sabe que le contestáis vosotros. Si lo quieres, marca «Contestar «Recibido» a lo que escribe»."));
      // «Cancelar la práctica» (semana_cambio.modo = "cancelar"): una lista con sus prácticas de ese aviso; al elegir
      // una se cancela (con la cancelación tardía) y te llega a Peticiones. Sin huecos ni espera de texto.
      if (ctx.cambio && ctx.cambio.modo === "cancelar") {
        const l = listaCancelar({ lang: ca ? "ca" : "es", una: ctx.origen === "asignada", tardia: ctx.tardia, corte: Number(ctx.corte && ctx.corte.horas) || 0 });
        return {
          ...vacio,
          mensajes: [{ texto: deNegocio(l.texto), buttons: l.filas, pie: l.boton }],
          crm: [nota("crmCancelarLista", "Le llega la lista de sus prácticas de ese aviso: elige cuál cancela (o ninguna). No puede cambiar de hora.")],
        };
      }
      // Con «Elige él un hueco libre de su profesor» (semana_cambio.modo = "huecos"): el formulario con las horas.
      // Si escribe en vez de abrirlo, su texto sigue llegando a Peticiones (la espera de texto se pone igual).
      if (ctx.cambio && ctx.cambio.modo === "huecos" && ctx.conext !== false) {
        return {
          ...vacio,
          mensajes: [{
            texto: deNegocio(textoFlowCambio({ movibles: ctx.origen === "asignada" ? 1 : 2, al_elegir: ctx.cambio.al_elegir })),
            flow: { cta: CAMBIO_TEXTOS.flowCta, pie: CAMBIO_TEXTOS.flowPie, id: "cambiar" },
          }],
          crm: [nota("crmCambioHuecos", "El alumno elige una hora libre de su profesor en el formulario. Si te escribe en vez de usarlo, su mensaje llega a Planificación → Peticiones.")],
          escribir: {
            ejemplo: ctx.origen === "asignada" ? A.ejemplo : S.ejemplo,
            respuesta: acuse,
            crm: crmCambio,
          },
        };
      }
      return {
        ...vacio,
        mensajes: [{ texto: deNegocio(ctx.origen === "asignada" ? A.quieroCambiar : S.quieroCambiar) }],
        escribir: {
          ejemplo: ctx.origen === "asignada" ? A.ejemplo : S.ejemplo,
          respuesta: acuse,
          crm: crmCambio,
        },
      };
    }
    case BOTON.pagar:
      return { ...vacio, url: "pago", crm: [nota("crmPagar", "El botón abre la página de pago segura (no escribe en el chat). Al pagar, el cobro se apunta solo en Pagos.")] };
    case BOTON.resena:
      return { ...vacio, url: "resena", crm: [nota("crmResena", "El botón abre tu ficha de Google para que deje la reseña (no escribe en el chat).")] };
    default:
      return vacio;
  }
}

/** Los conmutadores «Simular: …» que tienen sentido para unos botones. */
export function opcionesSimulacion(botones, ctx = {}) {
  const b = (Array.isArray(botones) ? botones : []).map(botonEs);
  const ops = [];
  if (b.includes(BOTON.cancelar) && ctx.tardia && ctx.tardia.enabled === true) {
    ops.push({
      id: "cancelaTarde",
      valores: [
        { v: false, k: "simConTiempo", d: "Cancela con tiempo" },
        { v: true, k: "simTarde", d: "Cancela tarde" },
      ],
    });
  }
  // Corte de cancelación del alumno (26/10/2026): anula con tiempo o a última hora (dentro del corte).
  const cancelaPorLista = b.includes(BOTON.quieroCambiar) && ctx.cambio && ctx.cambio.modo === "cancelar";
  if ((b.includes(BOTON.cancelar) || cancelaPorLista) && Number(ctx.corte && ctx.corte.horas) > 0) {
    ops.push({
      id: "dentroCorte",
      valores: [
        { v: false, k: "simCorteFuera", d: "Anula con tiempo" },
        { v: true, k: "simCorteDentro", d: "Anula a última hora" },
      ],
    });
  }
  // Con bono (9/10/2026): solo si la autoescuela trata distinto al que paga con bono (de fábrica, igual que sin él).
  if (b.includes(BOTON.cancelar) && ctx.tardia && ctx.tardia.enabled === true && (politicaBono(ctx.tardia) !== "cobrar" || politicaSinBono(ctx.tardia) !== "normal")) {
    ops.push({
      id: "conBono",
      valores: [
        { v: false, k: "simSinBono", d: "Paga con dinero" },
        { v: true, k: "simConBono", d: "Paga con bono" },
      ],
    });
  }
  if (b.includes(BOTON.loQuiero) || (Array.isArray(botones) && botones.some((x) => esBotonAdelanto(x) && (BOTON_ADELANTO.adelantar === x || x === "Avançar")))) {
    ops.push({
      id: "resultadoHueco",
      valores: [
        { v: "gana", k: "simGana", d: "Se la queda" },
        { v: "otro", k: "simOtro", d: "Ya la cogió otro" },
        { v: "plazo", k: "simPlazo", d: "Fuera de plazo" },
      ],
    });
  }
  return ops;
}

/** ¿Pulsar este botón hace algo en la vista previa? */
export const esInteractivo = (boton) => Object.values(BOTON).includes(boton) || boton === FLOW_PEDIR.cta || boton === FLOW_RESERVAR.cta
  || (boton in BOTON_CA && Object.values(BOTON).includes(BOTON_CA[boton])) || !!filaCancelar(boton) || esBotonExamen(boton)
  || esBotonAdelanto(boton);
